import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { MotionConfig } from "framer-motion";

const STORE = "data_saver"; // "on" | "off" | null (auto)
const DataSaverContext = createContext(null);

const conn = () => (typeof navigator !== "undefined" && (navigator.connection || navigator.mozConnection || navigator.webkitConnection)) || null;
export const slowNetwork = () => {
  const c = conn();
  return !!c && (c.saveData === true || ["slow-2g", "2g"].includes(c.effectiveType));
};

// "Mode Hemat Data": skips photos/maps and animations. Manual choice wins; otherwise follows the browser's Data Saver / 2G signal.
export const DataSaverProvider = ({ children }) => {
  const [pref, setPref] = useState(() => localStorage.getItem(STORE));
  const [slow, setSlow] = useState(slowNetwork);

  useEffect(() => {
    const c = conn();
    if (!c?.addEventListener) return;
    const onChange = () => setSlow(slowNetwork());
    c.addEventListener("change", onChange);
    return () => c.removeEventListener("change", onChange);
  }, []);

  const saver = pref === "on" || (pref !== "off" && slow);
  useEffect(() => { document.documentElement.classList.toggle("data-saver", saver); }, [saver]);

  const setSaver = useCallback((on) => { const v = on ? "on" : "off"; localStorage.setItem(STORE, v); setPref(v); }, []);
  const reset = useCallback(() => { localStorage.removeItem(STORE); setPref(null); }, []);

  const value = useMemo(() => ({ saver, pref, slow, setSaver, reset }), [saver, pref, slow, setSaver, reset]);
  return (
    <DataSaverContext.Provider value={value}>
      <MotionConfig reducedMotion={saver ? "always" : "user"}>{children}</MotionConfig>
    </DataSaverContext.Provider>
  );
};

export const useDataSaver = () => useContext(DataSaverContext) || { saver: false, pref: null, slow: false, setSaver: () => {}, reset: () => {} };
